import { useCallback, useEffect, useState } from 'react';
import AccountLayout from '../layouts/AccountLayout';
import './../styles/card-transfer.css';
import { ClipLoader, HashLoader } from 'react-spinners';
import ToastNotification, { showTopCenterToast } from '../components/toasts/ToastNotification';
import { getCardRatesForUser, sendCard } from '../api/payment.api';
import { useUser } from '../context/UserContext';
import socket from '../services/socket';
import { FiAlertTriangle, FiShield, FiZap } from 'react-icons/fi';
import { TbRefresh } from "react-icons/tb";
import { getCardDepositHistoryList } from '../api/transaction.api';
import { statusText } from '../utils';
import Pagination from '../components/pagination/Pagination';

const fallbackCardTypes = [
    { cardType: 1, cardName: 'Viettel' },
    { cardType: 3, cardName: 'Vina' },
    { cardType: 2, cardName: 'Mobi' },
    { cardType: 16, cardName: 'Vietnamobile' },
    { cardType: 14, cardName: 'Zing' },
    { cardType: 6, cardName: 'Garena' },
];

const fallbackAmounts = [10000, 20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000];
const CARD_HISTORY_PER_PAGE = 10;

const formatVND = (value) => `${Number(value || 0).toLocaleString("vi-VN")} đ`;
const formatRate = (value) => `${Number(value || 0).toFixed(2)}%`;

export const CardTransfer = () => {
    const [amount, setAmount] = useState(10000);
    const [seri, setSeri] = useState("");
    const [code, setCode] = useState("");
    const [cardType, setCardType] = useState(1);
    const [ratesPayload, setRatesPayload] = useState({ cards: [], estimate: { estimatedReceive: 0, appliedRate: 0 } });
    const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
    const { user, updateBalance } = useUser();

    const [loading, setLoading] = useState(false);
    const [loadingRates, setLoadingRates] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [cardHistory, setCardHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);

    const handleRechargeSuccess = useCallback(async ({ newBalance }) => {
        await updateBalance(newBalance);
    }, [updateBalance]);

    const handleDepositNotification = useCallback(async ({ success, error }) => {
        if (success) {
            showTopCenterToast("Nạp thành công hãy kiểm tra lịch sử giao dịch", "success");
            setLoading(false);
        } else {
            showTopCenterToast(`Nạp tiền lỗi: ${error}`, "error");
            setLoading(false);
        }
        
    }, []);

    const fetchCardHistory = useCallback(async (page = historyPage) => {
        setHistoryLoading(true);
        try {
            const res = await getCardDepositHistoryList(CARD_HISTORY_PER_PAGE, page);
            if (res?.success) {
                setCardHistory(res?.data?.transactions || []);
                setHistoryTotalPages(res?.data?.totalPages || 1);
                if (res?.data?.page && res.data.page !== historyPage) {
                    setHistoryPage(res.data.page);
                }
            }
        } catch (error) {
            showTopCenterToast(error.message || "Không thể tải lịch sử nạp thẻ", "error");
        } finally {
            setHistoryLoading(false);
        }
    }, [historyPage]);


    useEffect(() => {
        if (user?._id && socket) {
            socket.on("recharge_success", handleRechargeSuccess);
            socket.on("deposit-card-notification", handleDepositNotification);

            return () => {
                socket.off("recharge_success", handleRechargeSuccess);
                socket.off("deposit-card-notification", handleDepositNotification);
            };
        }
    }, [user?._id, handleRechargeSuccess, handleDepositNotification]);

    useEffect(() => {
        fetchCardHistory(historyPage);
    }, [historyPage, fetchCardHistory]);

    const fetchCardRates = useCallback(async (selectedCardType = cardType, selectedAmount = amount) => {
        try {
            setLoadingRates(true);
            const res = await getCardRatesForUser(selectedCardType, selectedAmount);
            if (res.success) {
                setRatesPayload(res.data || { cards: [], estimate: { estimatedReceive: 0, appliedRate: 0 } });
            }
        } catch (error) {
            showTopCenterToast(error.message, "error");
        } finally {
            setLoadingRates(false);
        }
    }, [cardType, amount]);

    useEffect(() => {
        fetchCardRates(cardType, amount);
    }, [cardType, amount, fetchCardRates]);

    useEffect(() => {
        const cards = ratesPayload?.cards || [];
        if (!cards.length || hasInitializedSelection) return;

        const firstCard = cards[0];
        const nextCardType = Number(firstCard.cardType || 0);
        const nextAmount = firstCard?.prices?.length
            ? Number(firstCard.prices[0].price || 0)
            : Number(amount || 10000);

        if (nextCardType > 0) {
            setCardType(nextCardType);
            setAmount(nextAmount);
            setHasInitializedSelection(true);
        }
    }, [ratesPayload?.cards, hasInitializedSelection, amount]);

    useEffect(() => {
        const cards = ratesPayload?.cards || [];
        if (!cards.length) return;

        const currentCard = cards.find((item) => Number(item.cardType) === Number(cardType));
        if (!currentCard) {
            setCardType(Number(cards[0].cardType));
            return;
        }

        if (currentCard.prices?.length) {
            const hasAmount = currentCard.prices.some((item) => Number(item.price) === Number(amount));
            if (!hasAmount) {
                setAmount(Number(currentCard.prices[0].price));
            }
        }
    }, [ratesPayload?.cards, cardType, amount]);

    const cardOptions = (ratesPayload?.cards?.length ? ratesPayload.cards : fallbackCardTypes)
        .map((item) => {
            const prices = item?.prices || [];
            const matchedByAmount = prices.find((priceItem) => Number(priceItem.price) === Number(amount));
            const fallbackFirstPrice = prices[0];
            const displayRate = Number(
                matchedByAmount?.rate
                ?? fallbackFirstPrice?.rate
                ?? item.defaultRate
                ?? 0
            );

            return {
                cardType: Number(item.cardType),
                cardName: item.cardName,
                displayRate,
            };
        });

    const selectedCard = (ratesPayload?.cards || []).find((item) => Number(item.cardType) === Number(cardType));
    const amountOptions = selectedCard?.prices?.length
        ? selectedCard.prices.map((item) => ({
            value: Number(item.price),
            rate: Number(item.rate || 0),
            receiveAmount: Number(item.receiveAmount || 0),
        }))
        : fallbackAmounts.map((value) => ({
            value: Number(value),
            rate: Number(ratesPayload?.estimate?.appliedRate || 0),
            receiveAmount: Math.floor(Number(value) * (100 - Number(ratesPayload?.estimate?.appliedRate || 0)) / 100),
        }));

    const selectedAmountItem = amountOptions.find((item) => Number(item.value) === Number(amount));
    const appliedRate = Number(
        selectedAmountItem?.rate
        ?? ratesPayload?.estimate?.appliedRate
        ?? 0
    );
    const estimatedReceive = Number(
        Math.floor(Number(amount) * (100 - appliedRate) / 100)
    );

    const handleCardDeposit = async () => {
        if (!seri || !code) {
            showTopCenterToast("Vui lòng nhập đầy đủ thông tin", "error");
            return;
        }

        if (seri.length > 50) {
            showTopCenterToast("Mã serial tối đa là 50 kí tự", "error");
            return;
        }

        if (code.length > 30) {
            showTopCenterToast("Mã code tối đa là 30 kí tự", "error");
            return;
        }

        setLoading(true);
        try {
            await sendCard(code, seri, cardType, amount);

            showTopCenterToast("Đã gửi thẻ, vui lòng đợi kiểm tra thẻ!", "warn");
        } catch (error) {
            showTopCenterToast(error.message, "error");
            setLoading(false);
        }
    }

    return (
        <AccountLayout title={'Nạp tiền bằng thẻ cào'}>
            <ToastNotification />
            <div className='card-transfer-page'>
                <section className='card-transfer-hero'>
                    <p className='card-transfer-eyebrow'>Card Gateway</p>
                    <h2>Nạp tiền bằng thẻ cào</h2>
                    <p className='card-transfer-subtitle'>
                        Gửi thẻ nhanh, theo dõi xử lý realtime và đảm bảo minh bạch chiết khấu từng nhà mạng.
                    </p>
                    <div className='card-discount-information-container'>
                        {cardOptions.map((item) => (
                            <div className='card-discount' key={`${item.cardType}-${item.cardName}`}>
                                <span className='card-discount-name'>{item.cardName}</span>
                                <span className='card-discount-value'>{Number(item.displayRate || 0).toFixed(2)}%</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className='card-transfer-note-box warning'>
                    <div className='card-transfer-note-title'>
                        <FiAlertTriangle className='card-transfer-inline-icon' />
                        <span>Lưu ý quan trọng</span>
                    </div>
                    <p className='caution-label'>
                        Sai mệnh giá hoặc sai khai báo có thể bị trừ đến 50% giá trị thẻ. Vui lòng nhập chính xác seri
                        và mã thẻ, các trường hợp cố tình sai để khiếu nại sẽ không được hỗ trợ.
                    </p>
                </section>

                <section className='card-transfer-note-box neutral'>
                    <div className='card-transfer-note-title'>
                        <FiShield className='card-transfer-inline-icon' />
                        <span>Khuyến nghị hệ thống</span>
                    </div>
                    <p className='caution-label'>
                        Sau khi gửi thẻ vui lòng chờ hệ thống xử lý, không reload liên tục. Nếu có giao dịch lỗi, hãy
                        liên hệ admin để được hỗ trợ nhanh nhất.
                    </p>
                </section>

                <section className='card-deposit-container'>
                    <div className='card-transfer-form-grid'>
                        <div className='card-form-field'>
                            <label htmlFor='card-type'>Loại thẻ</label>
                            <select
                                id='card-type'
                                value={cardType}
                                onChange={(e) => setCardType(Number(e.target.value))}
                                className='card-gateway-dropdown'
                            >
                                {cardOptions.map((item) => (
                                    <option key={item.cardType} value={item.cardType}>{item.cardName}</option>
                                ))}
                            </select>
                        </div>

                        <div className='card-form-field'>
                            <label htmlFor='card-amount'>Mệnh giá</label>
                            <select
                                id='card-amount'
                                value={amount}
                                onChange={(e) => setAmount(Number(e.target.value))}
                                className='card-gateway-dropdown'
                            >
                                {amountOptions.map(item => (
                                    <option key={item.value} value={item.value}>
                                        {formatVND(item.value)} - {formatRate(item.rate)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className='card-form-field'>
                            <label htmlFor='card-seri'>Số seri</label>
                            <input
                                id='card-seri'
                                value={seri}
                                onChange={(e) => setSeri(e.target.value)}
                                className='card-seri-input'
                                placeholder='Nhập số seri'
                            />
                        </div>

                        <div className='card-form-field'>
                            <label htmlFor='card-code'>Mã thẻ</label>
                            <input
                                id='card-code'
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className='card-seri-input'
                                placeholder='Nhập mã thẻ'
                            />
                        </div>
                    </div>

                    <div className='card-transfer-submit-row'>
                        <div className='card-transfer-estimate'>
                            <FiZap className='card-transfer-inline-icon' />
                            <span>{loadingRates ? "Đang cập nhật rate..." : "Dự kiến nhận:"}</span>
                            <strong>{formatVND(estimatedReceive)}</strong>
                        </div>
                        <button
                            onClick={handleCardDeposit}
                            disabled={loading || loadingRates || amount === 0}
                            className='card-deposit-button'
                        >
                            {loading ? <ClipLoader size={20} color='#fff' /> : "Gửi thẻ ngay"}
                        </button>
                    </div>

                    <CardDepositHistory
                        history={cardHistory}
                        loading={historyLoading}
                        onRefresh={() => fetchCardHistory(historyPage)}
                        currentPage={historyPage}
                        totalPages={historyTotalPages}
                        onPageChange={(page) => setHistoryPage(page)}
                    />
                </section>
            </div>
        </AccountLayout>
    )
}

const CardDepositHistory = ({ history, loading, onRefresh, currentPage, totalPages, onPageChange }) => {
    const getStatusClass = (status) => {
        const normalized = String(status || "").toLowerCase();
        if (normalized === "success" || normalized === "completed") return "is-success";
        if (normalized === "failed") return "is-failed";
        return "is-pending";
    };

    return (
        <section className="card-history-section">
            <div className="card-history-head">
                <div>
                    <h3>Lịch sử nạp thẻ cào</h3>
                    <p>Hiển thị các giao dịch nạp tiền bằng phương thức thẻ cào.</p>
                </div>
                <button
                    type="button"
                    className="card-history-refresh-btn"
                    onClick={onRefresh}
                    disabled={loading}
                >
                    <TbRefresh />
                    Làm mới
                </button>
            </div>

            {loading ? (
                <div className="card-history-loading">
                    <HashLoader color="var(--hash-loader-color)" size={26} />
                </div>
            ) : history.length === 0 ? (
                <p className="card-history-empty">Bạn chưa có giao dịch nạp thẻ cào.</p>
            ) : (
                <>
                    <div className="card-history-table-wrap">
                        <table className="card-history-table">
                            <thead>
                                <tr>
                                    <th>Mã giao dịch</th>
                                    <th>Số tiền</th>
                                    <th>Trạng thái</th>
                                    <th>Thời gian</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((item) => (
                                    <tr key={item.transactionId}>
                                        <td>{item.transactionId}</td>
                                        <td>
                                            <span className="card-history-amount">
                                                {Number(item.amount || 0).toLocaleString("vi-VN")}đ
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`card-history-status ${getStatusClass(item.transactionStatus)}`}>
                                                {statusText(item.transactionStatus) || item.transactionStatus || "-"}
                                            </span>
                                        </td>
                                        <td>{new Date(item.createdAt).toLocaleString("vi-VN")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange}
                        />
                    )}
                </>
            )}
        </section>
    );
};
